import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "../components/Header";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { toast } from "sonner";
import TwoFactorStep from "../components/TwoFactorStep";
import { SupplierKycFields, KycPendingNotice } from "../components/SupplierKycFields";

const goHome = (u, navigate) => {
  if (u.two_factor_mandatory && !u.totp_enabled) { toast.message("Aktifkan Google Authenticator untuk melanjutkan"); navigate("/account"); return; }
  navigate(u.role === "buyer" ? "/marketplace" : "/dashboard");
};

export function Login() {
  const { t } = useI18n();
  const { login, verify2fa } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [challenge, setChallenge] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      if (u.challenge) { setChallenge(u.challenge); return; }
      toast.success(`${t("auth.welcome_back")}, ${u.name}`);
      goHome(u, navigate);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("auth.login_failed"));
    } finally { setLoading(false); }
  };

  const verify = async ({ code, recovery }) => {
    setLoading(true);
    try {
      const u = await verify2fa(challenge, code, recovery);
      toast.success(`Welcome back, ${u.name}`);
      goHome(u, navigate);
    } catch (err) {
      const status = err?.response?.status;
      toast.error(err?.response?.data?.detail || "Verifikasi gagal");
      if (status === 401 && /login ulang/.test(err?.response?.data?.detail || "")) setChallenge(null);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#101114]">
      <Header />
      <div className="max-w-md mx-auto px-6 py-16">
        <h1 className="text-3xl font-bold text-[#F5F3ED] mb-2" style={{fontFamily:"'Playfair Display', serif"}}>{t("auth.login")}</h1>
        <div className="mb-8" />
        {challenge ? <TwoFactorStep onVerify={verify} onCancel={() => { setChallenge(null); setPassword(""); }} loading={loading} /> : (
        <form onSubmit={submit} className="bg-[#202127] border border-[#35353B] rounded-lg p-6 space-y-4">
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4AF37]">{t("auth.email")}</label>
            <input data-testid="login-email-input" type="email" required value={email} onChange={(e)=>setEmail(e.target.value)}
              className="mt-2 bg-[#191A1F] border border-[#35353B] text-[#F5F3ED] focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] rounded-sm placeholder-[#A8A8AD] px-4 py-2.5 w-full outline-none" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4AF37]">{t("auth.password")}</label>
            <input data-testid="login-password-input" type="password" required value={password} onChange={(e)=>setPassword(e.target.value)}
              className="mt-2 bg-[#191A1F] border border-[#35353B] text-[#F5F3ED] focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] rounded-sm placeholder-[#A8A8AD] px-4 py-2.5 w-full outline-none" />
          </div>
          <div className="text-right -mt-2">
            <Link to="/forgot-password" data-testid="forgot-password-link" className="text-xs text-[#D4AF37] hover:underline">{t("auth.forgot")}</Link>
          </div>
          <button data-testid="login-submit-btn" type="submit" disabled={loading}
            className="w-full bg-[#D4AF37] text-[#101114] hover:bg-[#F2D98D] disabled:opacity-50 transition-colors font-bold rounded-sm py-2.5">
            {loading ? t("common.loading") : t("auth.login")}
          </button>
          <p className="text-xs text-center text-[#A8A8AD]">{t("auth.no_account")} <Link to="/register" className="text-[#D4AF37] hover:underline">{t("auth.register")}</Link></p>
        </form>)}
      </div>
    </div>
  );
}

export function Register() {
  const { t } = useI18n();
  const { register, registerSupplier } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "buyer", accept_policy: false });
  const [kyc, setKyc] = useState({ telegram: "", whatsapp: "", ktp: null, selfie: null });
  const [loading, setLoading] = useState(false);
  const [pending, setPending] = useState(false);
  const isSupplier = form.role === "supplier";

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isSupplier) {
        await registerSupplier(form, kyc);
        setPending(true);
        return;
      }
      const u = await register(form);
      toast.success(`${t("auth.welcome")}, ${u.name}`);
      navigate(u.role === "buyer" ? "/marketplace" : "/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("auth.register_failed"));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#101114]">
      <Header />
      <div className="max-w-md mx-auto px-6 py-16">
        <h1 className="text-3xl font-bold text-[#F5F3ED] mb-8" style={{fontFamily:"'Playfair Display', serif"}}>{t("auth.register")}</h1>
        {pending ? <KycPendingNotice /> : (
        <form onSubmit={submit} className="bg-[#202127] border border-[#35353B] rounded-lg p-6 space-y-4">
          {[
            ["name", t("auth.name"), "text"],
            ["email", t("auth.email"), "email"],
            ["password", t("auth.password"), "password"],
          ].map(([k, label, type]) => (
            <div key={k}>
              <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4AF37]">{label}</label>
              <input data-testid={`register-${k}-input`} type={type} required value={form[k]} onChange={(e)=>setForm({...form, [k]: e.target.value})}
                className="mt-2 bg-[#191A1F] border border-[#35353B] text-[#F5F3ED] focus:border-[#D4AF37] rounded-sm px-4 py-2.5 w-full outline-none" />
            </div>
          ))}
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4AF37]">{t("auth.role")}</label>
            <select data-testid="register-role-select" value={form.role} onChange={(e)=>setForm({...form, role: e.target.value})}
              className="mt-2 bg-[#191A1F] border border-[#35353B] text-[#F5F3ED] rounded-sm px-4 py-2.5 w-full outline-none">
              <option value="buyer">{t("auth.role.buyer")}</option>
              <option value="supplier">{t("auth.role.supplier")}</option>
            </select>
          </div>
          {isSupplier && <SupplierKycFields kyc={kyc} setKyc={setKyc} />}
          <label data-testid="register-policy-label" className="flex items-start gap-2 text-xs text-[#A8A8AD]">
            <input data-testid="register-policy-checkbox" type="checkbox" required className="accent-[#D4AF37] mt-0.5" checked={form.accept_policy}
              onChange={(e) => setForm({ ...form, accept_policy: e.target.checked })} />
            <span>{t("auth.accept_prefix")}{" "}
              <Link to={`/policy/${form.role}`} target="_blank" data-testid="register-policy-link" className="text-[#D4AF37] hover:underline">{t(`auth.policy.${form.role}`)}</Link>
            </span>
          </label>
          <button data-testid="register-submit-btn" type="submit" disabled={loading}
            className="w-full bg-[#D4AF37] text-[#101114] hover:bg-[#F2D98D] disabled:opacity-50 transition-colors font-bold rounded-sm py-2.5">
            {loading ? t("common.loading") : t("auth.register")}
          </button>
          <p className="text-xs text-center text-[#A8A8AD]">{t("auth.have_account")} <Link to="/login" className="text-[#D4AF37] hover:underline">{t("auth.login")}</Link></p>
        </form>)}
      </div>
    </div>
  );
}
